(() => {
  'use strict';

  const formatMoney = (cents, format = '${{amount}}') => {
    const value = Number(cents || 0);
    const formatWithDelimiters = (amount, precision = 2, thousands = ',', decimal = '.') => {
      if (!Number.isFinite(amount)) return '0';
      const parts = (amount / 100).toFixed(precision).split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return parts.join(decimal);
    };

    const match = format.match(/\{\{\s*(\w+)\s*\}\}/);
    if (!match) return formatWithDelimiters(value);

    let amount;
    switch (match[1]) {
      case 'amount_no_decimals':
        amount = formatWithDelimiters(value, 0);
        break;
      case 'amount_with_comma_separator':
        amount = formatWithDelimiters(value, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        amount = formatWithDelimiters(value, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        amount = formatWithDelimiters(value, 2, "'", '.');
        break;
      default:
        amount = formatWithDelimiters(value, 2);
    }
    return format.replace(match[0], amount);
  };

  class ProductForm {
    constructor(form) {
      this.form = form;
      this.section = form.closest('[data-product-section]');
      this.variantInput = form.querySelector('[data-variant-id]');
      this.addButton = form.querySelector('[data-add-to-cart]');
      this.addButtonText = form.querySelector('[data-add-to-cart-text]');
      this.availability = form.querySelector('[data-product-availability]');
      this.price = form.querySelector('[data-product-price]');
      this.quantityInput = form.querySelector('[data-quantity-input]');
      this.quantityHidden = form.querySelector('[data-quantity-hidden]');
      this.variants = this.parseVariants();
      this.bind();
    }

    parseVariants() {
      const script = this.section?.querySelector('[data-product-variants]');
      if (!script) return [];
      try {
        return JSON.parse(script.textContent);
      } catch (error) {
        return [];
      }
    }

    bind() {
      this.form.addEventListener('change', (event) => {
        if (!event.target.matches('[data-option-value]')) return;
        this.onVariantChange();
      });

      this.form.addEventListener('click', (event) => {
        if (event.target.closest('[data-quantity-minus]')) {
          event.preventDefault();
          this.adjustQuantity(-1);
        }
        if (event.target.closest('[data-quantity-plus]')) {
          event.preventDefault();
          this.adjustQuantity(1);
        }
      });

      this.quantityInput?.addEventListener('input', () => this.syncQuantity());
      this.syncQuantity();
    }

    selectedOptions() {
      return [...this.form.querySelectorAll('[data-option-position]')].map((group) => {
        const selected = group.querySelector('[data-option-value]:checked');
        return selected?.value;
      });
    }

    currentVariant() {
      const options = this.selectedOptions();
      return this.variants.find(
        (variant) => variant.options.length === options.length && variant.options.every((value, index) => value === options[index])
      );
    }

    onVariantChange() {
      const variant = this.currentVariant();
      this.updateSelectedLabels();
      this.updateVariant(variant);
    }

    updateSelectedLabels() {
      this.form.querySelectorAll('[data-option-position]').forEach((group) => {
        const selected = group.querySelector('[data-option-value]:checked');
        const label = group.querySelector('[data-selected-option]');
        if (selected && label) label.textContent = selected.value;
      });
    }

    updateVariant(variant) {
      if (!variant) {
        if (this.variantInput) this.variantInput.value = '';
        this.setButton(false, window.NNL.strings.unavailable);
        if (this.availability) this.availability.lastChild.textContent = window.NNL.strings.unavailable;
        return;
      }

      this.variantInput.value = variant.id;
      this.setButton(variant.available, variant.available ? window.NNL.strings.addToCart : window.NNL.strings.soldOut);
      this.updatePrice(variant);
      this.updateAvailability(variant);
      this.updateUrl(variant);
      this.showFeaturedMedia(variant);
    }

    setButton(enabled, text) {
      if (this.addButton) this.addButton.disabled = !enabled;
      if (this.addButtonText) this.addButtonText.textContent = text;
    }

    updatePrice(variant) {
      if (!this.price) return;
      const current = formatMoney(variant.price, window.NNL.moneyFormat);
      const compare =
        variant.compare_at_price && variant.compare_at_price > variant.price
          ? `<s class="price__compare">${formatMoney(variant.compare_at_price, window.NNL.moneyFormat)}</s>`
          : '';
      this.price.innerHTML = `<span class="price__current">${current}</span>${compare}`;
    }

    updateAvailability(variant) {
      if (!this.availability) return;
      const dot = this.availability.querySelector('.product-availability__dot')?.outerHTML || '';
      this.availability.innerHTML = `${dot}${variant.available ? window.NNL.strings.available : window.NNL.strings.soldOut}`;
    }

    updateUrl(variant) {
      if (!history.replaceState) return;
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      history.replaceState({}, '', url);
    }

    showFeaturedMedia(variant) {
      const mediaId = variant.featured_media?.id;
      if (!mediaId) return;
      const target = this.section.querySelector(`[data-media-id="${mediaId}"]`);
      if (!target || window.innerWidth >= 990) return;
      target.scrollIntoView({
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        block: 'nearest',
        inline: 'start'
      });
    }

    adjustQuantity(direction) {
      if (!this.quantityInput) return;
      const step = Number(this.quantityInput.step) || 1;
      const min = Number(this.quantityInput.min) || 1;
      const max = Number(this.quantityInput.max) || Number.POSITIVE_INFINITY;
      const next = Math.min(max, Math.max(min, Number(this.quantityInput.value || min) + direction * step));
      this.quantityInput.value = String(next);
      this.syncQuantity();
    }

    syncQuantity() {
      if (!this.quantityInput || !this.quantityHidden) return;
      const min = Number(this.quantityInput.min) || 1;
      const quantity = Math.max(min, Number.parseInt(this.quantityInput.value, 10) || min);
      this.quantityHidden.value = String(quantity);
    }
  }

  const initialize = (scope = document) => {
    scope.querySelectorAll('form[data-product-form]:not([data-product-form-ready])').forEach((form) => {
      form.dataset.productFormReady = 'true';
      new ProductForm(form);
    });
  };

  document.addEventListener('DOMContentLoaded', () => initialize());
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
